import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useLocation, useHistory, Link } from "react-router-dom";
import Pagination from "react-js-pagination";
import Select from "react-select";
import ReactDatetime from "react-datetime";
import ReactBSAlert from "react-bootstrap-sweetalert";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInfo,
  faTimes,
  faUndo,
  faPause,
  faArrowUp,
  faArrowCircleUp,
} from "@fortawesome/free-solid-svg-icons";
import {
  Button,
  Card,
  UncontrolledTooltip,
  CardHeader,
  CardBody,
  Form,
  CardTitle,
  Input,
  Table,
  Container,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import subscriptionsService from "../../services/api/subscription-membershipl-service";
import projectsService from "../../services/api/projects-service";
import projectUsersService from "../../services/api/project-users-service";
import Projects from "../projects/Projects";
import AsyncSelect from "react-select/async";
import debounce from "lodash.debounce";

const RECORDS_PER_PAGE = 10;

const TYPE_OPTIONS = [
  { value: "onetime", label: "One time" },
  { value: "recurring", label: "Recurring" },
];

const STATUS_OPTIONS = [
  { value: "created", label: "Created" },
  { value: "authenticated", label: "Authenticated" },
  { value: "activated", label: "Activated" },
  { value: "failed", label: "Failed" },
  { value: "declined", label: "Declined" },
  { value: "suspended", label: "Suspended" },
  { value: "charged", label: "Charged" },
  { value: "expired", label: "Expired" },
  { value: "completed", label: "Completed" },
  { value: "pending", label: "Pending" },
  { value: "halted", label: "Halted" },
  { value: "cancelled", label: "Cancelled" },
  { value: "paused", label: "Paused" },
  { value: "resumed", label: "Resumed" },
];

const PLATFORM_OPTIONS = [
  { value: "web", label: "Web" },
  { value: "android", label: "Android" },
  { value: "iOS", label: "IOS" },
];

const CURRENCY_OPTIONS = [
  { value: "INR", label: "INR" },
  { value: "USD", label: "USD" },
];

const initialFormState = {
  user_id: "",
  subscription_id: "",
  from_date: "",
  to_date: "",
  start_at: "",
  end_at: "",
  status: ['authenticated','activated','charged'],
  platform: [],
  payment_gateway: [],
  type: "",
  plan_id: [],
  currency: "",
  paid_count: null
};

const Subscriptions = () => {
  const history = useHistory();
  const location = useLocation();
  const projectId = useMemo(
    () => new URLSearchParams(location.search).get("projectid"),
    [location.search]
  );

  const [subscriptions, setSubscriptions] = useState([]);
  const [totalSubscriptions, setTotalSubscriptions] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [projectDetails, setProjectDetails] = useState({});
  const [gateways, setGateways] = useState([]);
  const [planId, setPlanId] = useState([]);

  const initFromURL = () => {
    const urlParams = new URLSearchParams(location.search);

    const formFromURL = { ...initialFormState };

    Object.keys(initialFormState).forEach((key) => {
      const paramValue = urlParams.get(key);

      if (paramValue !== null) {
        if (Array.isArray(initialFormState[key])) {
          formFromURL[key] = paramValue ? paramValue.split(",") : [];
        } else {
          formFromURL[key] = paramValue;
        }
      }
    });

    formFromURL.project_id = projectId || "";

    return formFromURL;
  };

  const [formState, setFormState] = useState(initFromURL);

  // Store active filters in a ref to decouple form state from API requests
  const activeFiltersRef = useRef({ ...initFromURL() });

  const [activePage, setActivePage] = useState(() => {
    const urlParams = new URLSearchParams(location.search);
    return parseInt(urlParams.get("page") || "1", 10);
  });

  const updateURL = useCallback(() => {
    const params = new URLSearchParams();

    if (projectId) {
      params.set("projectid", projectId);
    }

    Object.entries(formState).forEach(([key, value]) => {
      if (key !== "project_id") {
        if (Array.isArray(value) && value.length > 0) {
          params.set(key, value.join(","));
        } else if (value && !Array.isArray(value)) {
          params.set(key, value);
        }
      }
    });

    params.set("page", activePage.toString());

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [formState, activePage, projectId, history, location.pathname]);

  useEffect(() => {
    updateURL();
  }, [formState, activePage, updateURL]);

  const handleClickUser = useCallback(
    (projectId, userId) => {
      if (!projectId || !userId) return;

      history.push(
        `/dashboard/user-detailed-view?userid=${userId}&projectid=${projectId}`
      );
    },
    [history]
  );

  const handleClickSubs = useCallback(
    (projectId, pg_subscription_id) => {
      if (!projectId || !pg_subscription_id) return;

      history.push(
        `/membership/transaction?pg_subscription_id=${pg_subscription_id}&projectid=${projectId}`
      );
    },
    [history]
  );

  useEffect(() => {
    if (!projectId) return;

    const fetchProjectDetails = async () => {
      try {
        const result = await projectsService.getProjectDetails(projectId);
        if (result?.status) {
          setProjectDetails(result.data);
        }
      } catch (error) {
        console.error("Error fetching project details:", error);
      }
    };

    fetchProjectDetails();
  }, [projectId]);

  const fetchGateways = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await subscriptionsService.getAllPaymentGateways();
      if (response.status === true && response.data) {
        const formattedGateways = response.data.map((gateway) => ({
          value: gateway,
          label: gateway
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
        }));
        setGateways(formattedGateways);
      }
    } catch (error) {
      console.error("Error fetching gateways:", error);
    }
  }, [projectId]);

  useEffect(() => {
    fetchGateways();
  }, [fetchGateways]);

  const fetchPlanId = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await subscriptionsService.getAllPlanId();
      if (response.status === true && response.data) {
        const formattedPlanId = response.data.map((planId) => ({
          value: planId,
          label: planId
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
        }));
        setPlanId(formattedPlanId);
      }
    } catch (error) {
      console.error("Error fetching gateways:", error);
    }
  }, [projectId]);

  useEffect(() => {
    fetchPlanId();
  }, [fetchPlanId]);

const debouncedFetchUserOptions = debounce(async (inputValue, callback, projectId, setUserOptions) => {
  try {
    const users = await projectUsersService.fetchUsers({
      project_id: projectId,
      search: inputValue,
      page: 1,
      page_size: "",
    });

    if (users?.status && users?.data?.users) {
      const options = users.data.users.map((user) => ({
        value: user.user_id,
        label: user.email
          ? `${user.name} (${user.email})`
          : `${user.name} ${user.phone_number}`,
        email: user.email,
      }));

      setUserOptions(options);      // Store for default value display
      callback(options);            // Return to AsyncSelect
    } else {
      setUserOptions([]);
      callback([]);
    }
  } catch (error) {
    console.error("Error fetching users:", error);
    setUserOptions([]);
    callback([]);
  }
}, 500);


const fetchUserOptions = useCallback(
  (inputValue) =>
    new Promise((resolve) => {
      if (!projectId) return resolve([]);
      debouncedFetchUserOptions(inputValue, resolve, projectId, setUserOptions);
    }),
  [projectId, setUserOptions]
);

  // Modified fetchSubscriptions to use activeFiltersRef instead of formState
  const fetchSubscriptions = useCallback(async () => {
    if (!projectId) return;

    setIsLoading(true);
    try {
      const response = await subscriptionsService.getSubscriptions(
        activeFiltersRef.current, // Use active filters instead of formState
        activePage,
        RECORDS_PER_PAGE
      );

      if (response.status && response.data) {
        setSubscriptions(response.data.subscriptions);
        setTotalSubscriptions(response.data.count);

        // Calculate max pages based on total count
        const maxPages = Math.ceil(response.data.count / RECORDS_PER_PAGE);

        // If current page is higher than max pages and max pages is not zero,
        // reset to the last available page
        if (activePage > maxPages && maxPages > 0) {
          setActivePage(maxPages);
          // Re-fetch with corrected page number
          const newResponse = await subscriptionsService.getSubscriptions(
            activeFiltersRef.current, // Use active filters here too
            maxPages,
            RECORDS_PER_PAGE
          );
          if (newResponse.status && newResponse.data) {
            setSubscriptions(newResponse.data.subscriptions);
            setTotalSubscriptions(newResponse.data.count);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      setAlert(createAlert("error", "Failed to fetch subscriptions"));
    } finally {
      setIsLoading(false);
    }
  }, [projectId, activePage]); // Removed formState dependency

  // Initial data load on component mount
  useEffect(() => {
    if (projectId) {
      // Set initial active filters
      activeFiltersRef.current = { ...formState };
      fetchSubscriptions();
    }
  }, [projectId]); // Run only when projectId changes or component mounts

  // Add effect to handle page changes
  useEffect(() => {
    // Only fetch if we have active filters already set
    // This prevents double fetching on component mount
    if (projectId && Object.keys(activeFiltersRef.current).length > 0) {
      fetchSubscriptions();
    }
  }, [activePage, fetchSubscriptions, projectId]);

  const handleFormChange = useCallback((field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleFilterReset = useCallback(() => {
    const resetState = { ...initialFormState, project_id: projectId };

    setFormState(resetState);
    setActivePage(1);

    // Update the active filters with reset values
    activeFiltersRef.current = { ...resetState };

    const params = new URLSearchParams();
    if (projectId) {
      params.set("projectid", projectId);
    }

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });

    // Explicitly fetch after reset
    fetchSubscriptions();
  }, [projectId, history, location.pathname, fetchSubscriptions]);

  const handleFilterSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setActivePage(1);

      // Update active filters with current form state
      activeFiltersRef.current = { ...formState };

      // Then fetch with updated filters
      fetchSubscriptions();
    },
    [fetchSubscriptions, formState]
  );

  const handlePageChange = useCallback((pageNumber) => {
    setActivePage(pageNumber);
    // Fetch will happen via the useEffect that watches activePage
  }, []);

  const confirmCancel = useCallback((subscription) => {
    setAlert(
      <ReactBSAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title="Are you sure?"
        onConfirm={() =>
          handleSubscriptionCancel(subscription.pg_subscription_id)
        }
        onCancel={() => setAlert(null)}
        confirmBtnBsStyle="info"
        cancelBtnBsStyle="danger"
        confirmBtnText="Yes, Cancel it!"
        cancelBtnText="Back"
        showCancel
      >
        <p>You want to cancel this Membership!</p>
        <hr />
        <p>
          <strong>Email:</strong> {subscription.user_id?.email || "N/A"}
        </p>
        <p>
          <strong>Subscription ID:</strong>{" "}
          {subscription.pg_subscription_id || "N/A"}
        </p>
        <p>
          <strong>Paid Count:</strong> {subscription.paid_count}
        </p>
        <p>
          <strong>Type:</strong>

          {subscription?.type
            ? subscription.type === "onetime"
              ? "One Time"
              : "Recurring"
            : subscription?.total_count === 1
            ? "One Time"
            : subscription?.total_count
            ? "Recurring"
            : "N/A"}
        </p>
        <p>
          <strong>Status:</strong> {subscription.status}
        </p>
      </ReactBSAlert>
    );
  }, []);

  const confirmStatusChange = useCallback((subscription) => {
    const isFailed = subscription.is_suspended === true;

    setAlert(
      <ReactBSAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title="Are you sure?"
        onConfirm={() =>
          handleSubscriptionStatus(subscription.pg_subscription_id)
        }
        onCancel={() => setAlert(null)}
        confirmBtnBsStyle={isFailed ? "success" : "info"}
        cancelBtnBsStyle="danger"
        confirmBtnText={isFailed ? "Yes, Restore it!" : "Yes, Suspend it!"}
        cancelBtnText="Back"
        showCancel
      >
        <p>You want to {isFailed ? "restore" : "Suspend"} this Membership!</p>
        <hr />
        <p>
          <strong>Email:</strong> {subscription.user_id?.email || "N/A"}
        </p>
        <p>
          <strong>Subscription ID:</strong>{" "}
          {subscription.pg_subscription_id || "N/A"}
        </p>
        <p>
          <strong>Paid Count:</strong> {subscription.paid_count}
        </p>
        <p>
          <strong>Type:</strong>{" "}
          {subscription?.type
            ? subscription.type === "onetime"
              ? "One Time"
              : "Recurring"
            : subscription?.total_count === 1
            ? "One Time"
            : subscription?.total_count
            ? "Recurring"
            : "N/A"}
        </p>
        <p>
          <strong>Status:</strong> {subscription.status}
        </p>
      </ReactBSAlert>
    );
  }, []);

  const handleSubscriptionCancel = useCallback(
    async (subscriptionId) => {
      setAlert(null);
      try {
        setAlert(createAlert("loading", "Cancelling your Membership..."));
        const response = await subscriptionsService.cancelSubscription(
          subscriptionId
        );

        if (response.status) {
          setAlert(createAlert("success", "Membership cancelled successfully"));

          await fetchSubscriptions();
        }
      } catch (error) {
        console.error("Error canceling Membership:", error);
        setAlert(
          createAlert("error", "Failed to cancel Membership. Please try again.")
        );
      }
    },
    [fetchSubscriptions]
  );

  const handleSubscriptionStatus = useCallback(
    async (subscriptionId) => {
      setAlert(null);
      try {
        setAlert(createAlert("loading", "Cancelling your Membership..."));
        const response = await subscriptionsService.changeStatus(
          subscriptionId
        );
        if (response.status) {
          setAlert(
            createAlert("success", "Status has been changed successfully")
          );

          await fetchSubscriptions();
        }
      } catch (error) {
        console.error("Error canceling Membership:", error);
        setAlert(
          createAlert("error", "Failed to cancel Membership. Please try again.")
        );
      }
    },
    [fetchSubscriptions]
  );

  const createAlert = useCallback((type, message) => {
    const alertProps = {
      style: { display: "block", marginTop: "-100px" },
      onConfirm: () => setAlert(null),
      onCancel: () => setAlert(null),
    };

    switch (type) {
      case "loading":
        return (
          <ReactBSAlert
            info
            {...alertProps}
            showConfirm={false}
            title="Processing..."
          >
            <div className="d-flex flex-column align-items-center">
              <Spinner className="mb-3" />
              <p>{message}</p>
            </div>
          </ReactBSAlert>
        );
      case "success":
        return (
          <ReactBSAlert
            success
            {...alertProps}
            title="Success"
            confirmBtnBsStyle="success"
          >
            {message}
          </ReactBSAlert>
        );
      case "error":
        return (
          <ReactBSAlert
            danger
            {...alertProps}
            title="Error"
            confirmBtnBsStyle="danger"
          >
            {message}
          </ReactBSAlert>
        );
      default:
        return null;
    }
  }, []);

  if (!projectId) {
    return <Projects navigate="membership/subscription" feature="membership" />;
  }

  return (
    <div className="content">
      {alert}
      <Container>
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center">
                    <CardTitle tag="h4">Membership Dashboard</CardTitle>
                    <span
                      className="text-muted "
                      style={{ marginLeft: "5px", marginTop: "5px" }}
                    >
                      {projectDetails.name
                        ? `(${projectDetails.name})`
                        : "(Project Name)"}
                    </span>
                  </div>

                  <div className="d-flex align-items-center">
  {/* Sales Dashboard Box */}
  <Link
    to={`sales-membership?projectid=${projectId}`}
    className="d-flex align-items-center text-info border rounded px-3 py-1 mr-2"
    title="Go to Sales Dashboard"
    style={{ textDecoration: 'none' }}
  >
    <span className="mr-2">Sales Dashboard</span>
    <FontAwesomeIcon   style={{ cursor: 'pointer', marginLeft: '10px' }} icon={faArrowCircleUp} />
  </Link>

  {/* Stats Box */}
  <span
    className="d-flex align-items-center border rounded px-3 py-1 text-dark"
    style={{ cursor: 'pointer', marginLeft: '10px' }}
    onClick={() => history.push(`/membership/daily-stats/${projectId}`)}
    title="View Stats"
  > 
    <span className="mr-2"  style={{ cursor: 'pointer', marginRight: '5px' }}>Stats</span>
  
    <i className="fa fa-bar-chart mr-2" aria-hidden="true"></i>
   
  </span>
</div>

                </div>

                <Form onSubmit={handleFilterSubmit}>
                  <Row>
                    <Col md="3">
                      <AsyncSelect
                        isClearable
                        placeholder="Select User"
                        defaultOptions={false}
                        loadOptions={fetchUserOptions} // now debounced
                        onChange={(option) =>
                          handleFormChange("user_id", option?.value || "")
                        }
                        noOptionsMessage={({ inputValue }) =>
                          inputValue ? "No users found" : "Search to get options"
                        }
                        value={
                          formState.user_id
                            ? {
                              value: formState.user_id,
                              label:
                                userOptions.find((u) => u.value === formState.user_id)?.label ||
                                "Selected User",
                            }
                            : null
                        }
                      />
                    </Col>

                    <Col md="3">
                      <ReactDatetime
                        value={formState.from_date}
                        onChange={(date) =>
                          handleFormChange("from_date", date._d)
                        }
                        inputProps={{
                          className: "form-control",
                          placeholder: "Created At : From Date",
                        }}
                      />
                    </Col>
                    <Col md="3">
                      <ReactDatetime
                        value={formState.to_date}
                        onChange={(date) =>
                          handleFormChange("to_date", date._d)
                        }
                        inputProps={{
                          className: "form-control",
                          placeholder: "Created At : To Date",
                        }}
                      />
                    </Col>
                    <Col md="3">
                      <Input
                        type="text"
                        placeholder="Membership ID"
                        value={formState.subscription_id}
                        onChange={(e) =>
                          handleFormChange("subscription_id", e.target.value)
                        }
                      />
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md="3">
                      <Select
                        isMulti
                        isClearable
                        placeholder="Select Status"
                        options={STATUS_OPTIONS}
                        value={STATUS_OPTIONS.filter((option) =>
                          formState.status.includes(option.value)
                        )}
                        onChange={(selectedOptions) =>
                          handleFormChange(
                            "status",
                            selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : []
                          )
                        }
                      />
                    </Col>
                    <Col md="3">
                      <Select
                        isMulti
                        isClearable
                        placeholder="Select Platform"
                        options={PLATFORM_OPTIONS}
                        value={PLATFORM_OPTIONS.filter((option) =>
                          formState.platform.includes(option.value)
                        )}
                        onChange={(selectedOptions) =>
                          handleFormChange(
                            "platform",
                            selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : []
                          )
                        }
                      />
                    </Col>
                    <Col md="3">
                      <Select
                        isMulti
                        isClearable
                        placeholder="Select Payment Gateway"
                        options={gateways}
                        value={gateways.filter((option) =>
                          formState.payment_gateway.includes(option.value)
                        )}
                        onChange={(selectedOptions) =>
                          handleFormChange(
                            "payment_gateway",
                            selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : []
                          )
                        }
                      />
                    </Col>
                    <Col md="3">
                      <Select
                        isClearable
                        placeholder="Membership Type"
                        options={TYPE_OPTIONS}
                        value={
                          TYPE_OPTIONS.find(
                            (option) => option.value === formState.type
                          ) || null
                        }
                        onChange={(option) =>
                          handleFormChange("type", option?.value)
                        }
                      />
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col md="3">
                      <Select
                        isMulti
                        isClearable
                        placeholder="Select Plan Id"
                        options={planId}
                        value={planId.filter((option) =>
                          formState.plan_id.includes(option.value)
                        )}
                        onChange={(selectedOptions) =>
                          handleFormChange(
                            "plan_id",
                            selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : []
                          )
                        }
                      />
                    </Col>

                    <Col md="3">
                      <Select
                        isClearable
                        placeholder="Currency"
                        options={CURRENCY_OPTIONS}
                        value={
                          CURRENCY_OPTIONS.find(
                            (option) => option.value === formState.currency
                          ) || null
                        }
                        onChange={(option) =>
                          handleFormChange("currency", option?.value)
                        }
                      />
                    </Col>

                    <Col md="3">
                      <ReactDatetime
                        value={formState.start_at}
                        onChange={(date) =>
                          handleFormChange("start_at", date._d)
                        }
                        inputProps={{
                          className: "form-control",
                          placeholder: "Start At",
                        }}
                      />
                    </Col>
                    <Col md="3">
                      <ReactDatetime
                        value={formState.end_at}
                        onChange={(date) => handleFormChange("end_at", date._d)}
                        inputProps={{
                          className: "form-control",
                          placeholder: "End At",
                        }}
                      />
                    </Col>
                  </Row>

                  <Row className="mt-3 align-items-center">
                    <Col md="3">
                      <Input
                        type="number"
                        placeholder="Paid Count"
                        value={formState?.paid_count || ""}
                        onChange={(e) => handleFormChange("paid_count", e.target.value)}
                      />
                    </Col>
                    <Col md="6">
                      <div className="d-flex gap-2 align-items-center">
                        <Button className="btn-round" color="info" type="submit">
                          Search
                        </Button>
                        <Button
                          className="btn-round"
                          color="warning"
                          type="button"
                          onClick={handleFilterReset}
                        >
                          Reset
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  
                </Form>
                {totalSubscriptions > 0 ? (
                  <Row>
                    <Col md="6">
                      <div style={{ float: "right", margin: "5px" }}>
                        <b>
                          {" "}
                          {(activePage - 1) * RECORDS_PER_PAGE + 1} -{" "}
                          {Math.min(
                            activePage * RECORDS_PER_PAGE,
                            totalSubscriptions
                          )}{" "}
                          of {totalSubscriptions}
                        </b>
                      </div>
                    </Col>
                    <Col md="6">
                      <div style={{ float: "left" }}>
                        <Pagination
                          activePage={activePage}
                          itemsCountPerPage={RECORDS_PER_PAGE}
                          totalItemsCount={totalSubscriptions}
                          pageRangeDisplayed={3}
                          onChange={handlePageChange}
                          itemClass="page-item"
                          linkClass="page-link"
                        />
                      </div>
                    </Col>
                  </Row>
                ) : null}
              </CardHeader>

              <CardBody>
                <Table responsive>
                  <thead className="text-primary">
                    <tr>
                      <th>S.no.</th>
                      <th>User Email or Phone No.</th>
                      <th>Membership ID</th>
                      <th>Type / Payment Gateway </th>
                      <th>Status / Plan ID / Amount</th>
                      <th>Created At</th>
                      <th>Current End</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="8" className="text-center">
                          <Spinner />
                        </td>
                      </tr>
                    ) : subscriptions.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center">
                          <CardTitle tag="h4">No Records Found</CardTitle>
                        </td>
                      </tr>
                    ) : (
                      subscriptions.map((subscription, index) => (
                        <tr key={subscription._id}>
                          <td>
                            {(activePage - 1) * RECORDS_PER_PAGE + index + 1}
                          </td>
                          <td
                            style={{ cursor: "pointer" }}
                            className="text-left "
                            onClick={() =>
                              handleClickUser(
                                projectId,
                                subscription?.user_id?._id
                              )
                            }
                          >
                            {subscription?.user_id?.email ||
                            subscription?.user_id?.phone_number ? (
                              <>
                                {subscription?.user_id?.email && (
                                  <div>{subscription.user_id.email}</div>
                                )}
                                {subscription?.user_id?.phone_number && (
                                  <div>{subscription.user_id.phone_number}</div>
                                )}
                              </>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td
                            style={{ cursor: "pointer", color: "#0077b6" }}
                            onClick={() =>
                              handleClickSubs(
                                projectId,
                                subscription?.pg_subscription_id
                              )
                            }
                          >
                            {subscription.pg_subscription_id || ""}
                          </td>

                          <td>
                            <div>
                              {subscription?.type
                                ? subscription.type === "onetime"
                                  ? "One Time"
                                  : "Recurring"
                                : subscription?.total_count === 1
                                ? "One Time"
                                : subscription?.total_count
                                ? "Recurring"
                                : "N/A"}
                            </div>
                            <div>{subscription.payment_gateway}</div>
                          </td>
                          <td>
                            <div>{subscription.status}</div>
                            <div>{subscription.plan_id}</div>
                            <div className="text-left">
                              {subscription?.currency === "INR" ? "₹" : "$"}
                              {(subscription?.amount || 0) / 100}
                            </div>
                          </td>
                          <td>
                            {subscription?.createdAt
                              ? moment(subscription?.createdAt).format(
                                  "DD MMM YYYY, hh:mm A"
                                )
                              : ""}
                          </td>

                          <td>
                            {subscription?.current_end ? (
                              <div>
                                {moment
                                  .unix(subscription.current_end)
                                  .format("DD MMM YYYY, hh:mm A")}
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>

                          <td>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              {/* Info Button with Tooltip - unique ID with subscription ID */}
                              <Button
                                id={`info-tooltip-${subscription._id}`}
                                color="info"
                                size="sm"
                                onClick={() =>
                                  history.push(
                                    `/membership/subscription-details/${subscription._id}`
                                  )
                                }
                              >
                                <FontAwesomeIcon icon={faInfo} />
                              </Button>
                              <UncontrolledTooltip
                                placement="top"
                                target={`info-tooltip-${subscription._id}`}
                              >
                                View subscription details
                              </UncontrolledTooltip>

                              <Button
                                id={`status-tooltip-${subscription._id}`}
                                color={
                                  !subscription?.is_suspended
                                    ? "warning"
                                    : "success"
                                }
                                size="sm"
                                onClick={() =>
                                  confirmStatusChange(subscription)
                                }
                              >
                                {!subscription?.is_suspended ? (
                                  <FontAwesomeIcon icon={faPause} />
                                ) : (
                                  <FontAwesomeIcon icon={faUndo} />
                                )}
                              </Button>
                              <UncontrolledTooltip
                                placement="top"
                                target={`status-tooltip-${subscription._id}`}
                              >
                                {!subscription?.is_suspended
                                  ? "Suspend Subscription"
                                  : "Restore subscription"}
                              </UncontrolledTooltip>

                              {/* Cancel Button with Tooltip - unique ID */}
                              {subscription.status !== "cancelled" &&
                                subscription.share_url !== "" &&
                                !subscription.pg_subscription_id?.startsWith(
                                  "order_"
                                ) && (
                                  <>
                                    <Button
                                      id={`cancel-tooltip-${subscription._id}`}
                                      color="danger"
                                      size="sm"
                                      onClick={() =>
                                        confirmCancel(subscription)
                                      }
                                    >
                                      <FontAwesomeIcon icon={faTimes} />
                                    </Button>
                                    <UncontrolledTooltip
                                      placement="top"
                                      target={`cancel-tooltip-${subscription._id}`}
                                    >
                                      Cancel subscription
                                    </UncontrolledTooltip>
                                  </>
                                )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Subscriptions;
